import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { createTask, getProperties } from '../lib/api';

const CATEGORIES = [
  { value: 'ARRIVAL', label: 'Arrival' },
  { value: 'ACCESS', label: 'Access' },
  { value: 'MAINTENANCE', label: 'Maintenance' },
  { value: 'HOUSEKEEPING', label: 'Housekeeping' },
  { value: 'TRANSPORT', label: 'Transport' },
  { value: 'BOOKING', label: 'Booking' },
  { value: 'CONCIERGE', label: 'Concierge' },
  { value: 'BILLING', label: 'Billing' },
  { value: 'OTHER', label: 'Other' },
];

const CHANNELS = [
  { value: 'WhatsApp', label: 'WhatsApp' },
  { value: 'Email', label: 'Email' },
  { value: 'Airbnb', label: 'Airbnb' },
  { value: 'Phone', label: 'Phone' },
  { value: 'Other', label: 'Other' },
];

export const LogRequestModal = ({ open, onOpenChange, onCreated, defaultPropertyId }) => {
  const [properties, setProperties] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    propertyId: defaultPropertyId || '',
    category: '',
    summary: '',
    guest_name_text: '',
    source_channel: '',
    dueAt: '',
    isHandleNow: false,
    notes: '',
  });

  useEffect(() => {
    if (open) {
      getProperties().then(r => setProperties(r.data)).catch(() => {});
      setForm(f => ({ ...f, propertyId: defaultPropertyId || f.propertyId }));
    }
  }, [open, defaultPropertyId]);

  const handleSave = async () => {
    if (!form.propertyId || !form.category || !form.summary.trim()) {
      toast.error('Property, category, and summary are required.');
      return;
    }
    setSaving(true);
    try {
      let dueAt = null;
      if (form.dueAt) {
        dueAt = new Date(form.dueAt).toISOString();
      }
      await createTask({
        propertyId: form.propertyId,
        type: 'LOG_REQUEST',
        category: form.category,
        priority: form.isHandleNow ? 'HANDLE_NOW' : 'DUE_TODAY',
        summary: form.summary.trim(),
        guest_name_text: form.guest_name_text || null,
        source_channel: form.source_channel || null,
        dueAt,
        notes: form.notes || null,
      });
      toast.success('Request logged.');
      onCreated?.();
      onOpenChange(false);
      setForm({ propertyId: defaultPropertyId || '', category: '', summary: '', guest_name_text: '', source_channel: '', dueAt: '', isHandleNow: false, notes: '' });
    } catch {
      toast.error('Failed to log request');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="log-request-modal">
        <DialogHeader>
          <DialogTitle className="font-heading">Log request</DialogTitle>
          <DialogDescription>Quick intake — operator logs a guest request or task.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs font-medium">Property *</Label>
              <Select value={form.propertyId} onValueChange={v => setForm({...form, propertyId: v})}>
                <SelectTrigger className="h-9" data-testid="log-request-property">
                  <SelectValue placeholder="Select property" />
                </SelectTrigger>
                <SelectContent>
                  {properties.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-medium">Category *</Label>
              <Select value={form.category} onValueChange={v => setForm({...form, category: v})}>
                <SelectTrigger className="h-9" data-testid="log-request-category">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-medium">Short summary *</Label>
            <Input
              value={form.summary}
              onChange={e => setForm({...form, summary: e.target.value})}
              placeholder="What happened or what's needed?"
              data-testid="log-request-summary"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Guest name</Label>
              <Input value={form.guest_name_text} onChange={e => setForm({...form, guest_name_text: e.target.value})} placeholder="Optional" data-testid="log-request-guest" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Channel</Label>
              <Select value={form.source_channel} onValueChange={v => setForm({...form, source_channel: v})}>
                <SelectTrigger className="h-9" data-testid="log-request-channel">
                  <SelectValue placeholder="Optional" />
                </SelectTrigger>
                <SelectContent>
                  {CHANNELS.map(c => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Due</Label>
              <Input type="datetime-local" value={form.dueAt} onChange={e => setForm({...form, dueAt: e.target.value})} className="h-9 text-sm" data-testid="log-request-due" />
            </div>
            <div className="flex items-center gap-2 pt-5">
              <Switch id="handle-now" checked={form.isHandleNow} onCheckedChange={v => setForm({...form, isHandleNow: v})} className="scale-90" />
              <Label htmlFor="handle-now" className="text-xs cursor-pointer">Handle Now</Label>
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Notes</Label>
            <Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder="Optional context" rows={2} data-testid="log-request-notes" />
          </div>
          <div className="flex gap-3 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1 rounded-full">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="flex-1 bg-[#3F617E] hover:bg-[#345470] rounded-full" data-testid="log-request-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              {saving ? 'Saving...' : 'Log request'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
